import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "./Layout";
import FileUploadModal from "./FileUploadModal";
import NoAlertsState from "./NoAlertsState";
import DateRangePicker from "./DateRangePicker";
import ThreatDetailsModal from './ThreatDetailsModal';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useOrganization } from '../context/OrganizationContext';
import CybeSelect from "./CybeSelect";

const formatDate = (value) => {
    if (!value) return '-';
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
};

const formatMergedAlert = (item, source = 'platform', idx = 0) => {
    if (source === 'syslog') {
        return {
            id: item.alert_id || `syslog-${idx}`,
            date: item.created_at || '',
            threatType: item.threat_type || 'Syslog Alert',
            severity: item.severity || 'unknown',
            source: item.source || 'syslog',
            sourceIp: item.client_ip || '-',
            matchedRule: item.matched_rule || '-',
            status: item.status || 'open',
            message: item.message || '-',
            country: item.country || '-',
            asn: item.asn || '-',
            isp: item.isp || '-',
            threatScore: item.threat_score ?? 0,
            reputationLabel: item.reputation_label || '-',
            total_reports: item.total_reports || 0,
            last_reported_at: item.last_reported_at || null,
            collectorDevice: item.collector_device_name || '-',
            sourceDevice: item.source_device || '-',
            flags: [
                item.is_vpn ? 'VPN' : null,
                item.is_proxy ? 'Proxy' : null,
                item.is_tor ? 'TOR' : null,
                item.is_datacenter ? 'DC' : null,
            ].filter(Boolean).join(', ') || 'None',
            isSyslog: true,
        };
    }

    return {
        id: item.id || item.alert_id || `platform-${idx}`,
        date: item.date || item.created_at || item.timestamp || '',
        threatType: item.threatType || item.threat_type || 'Platform Alert',
        severity: item.severity || 'unknown',
        source: 'platform',
        sourceIp: item.ip || item.client_ip || '-',
        matchedRule: item.matched_rule || '-',
        status: item.status || 'open',
        message: item.evidence || item.message || item.description || '-',
        country: item.country || '-',
        asn: item.asn || '-',
        isp: item.isp || '-',
        threatScore: item.threat_score ?? 0,
        reputationLabel: item.reputation_label || '-',
            total_reports: item.total_reports || 0,
            last_reported_at: item.last_reported_at || null,
        collectorDevice: item.collector_device_name || item.device_name || '-',
        sourceDevice: item.source_device || item.device_name || '-',
        flags: [
            item.is_vpn ? 'VPN' : null,
            item.is_proxy ? 'Proxy' : null,
            item.is_tor ? 'TOR' : null,
            item.is_datacenter ? 'DC' : null,
        ].filter(Boolean).join(', ') || 'None',
        isSyslog: false,
    };
};

const Alerts = ({ onLogout }) => {
    const { activeOrganization } = useOrganization();
    const [uploadHistory, setUploadHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('all-threats');
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedThreat, setSelectedThreat] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [uploadHistoryError, setUploadHistoryError] = useState(null);
    const [alertsError, setAlertsError] = useState(null);

    const [filters, setFilters] = useState({
        dateRange: {
            start: null,
            end: null,
        },
        severity: '',
        threatType: '',
        source: '',
        status: '',
        device: '',
        sourceIp: '',
    });

    const itemsPerPage = 10;
    const severityOptions = ["All", "High", "Medium", "Low"];
    const threatTypeOptions = [
        "All", "Benign", "BOT", "DDoS", "DoS GoldenEye", "DoS Hulk",
        "DoS Slowhttptest", "DoS Slowloris", "FTP-Patator", "Heartbleed",
        "Infiltration", "PortScan", "SSH-Patator", "Web Attack - Brute Force",
        "Sql Injection", "Web Attack XSS", "Port Scan", "Brute Force Attempt",
        "Blocked Connection", "DoS Activity"
    ];
    const sourceOptions = ["All", "platform", "syslog"];
    const statusOptions = ["All", "Open", "Investigating", "Resolved"];
    const deviceOptions = Array.from(new Set(alerts.flatMap((alert) => [alert.collectorDevice, alert.sourceDevice]).filter((value) => value && value !== '-'))).sort();

    useEffect(() => {
        if (!activeOrganization?.guid) {
            setAlerts([]);
            setIsLoading(false);
            return undefined;
        }

        fetchAlerts();
        const timer = setInterval(fetchAlerts, 15000);
        return () => clearInterval(timer);
    }, [activeOrganization?.guid]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        if (tab === 'upload-history') {
            fetchUploadHistory();
        }
    };

    const fetchAlerts = async () => {
        try {
            setIsLoading(true);
            setAlertsError(null);

            const token = Cookies.get('token');
            const userId = Cookies.get('guid');

            if (!token || !userId) {
                console.error("No authentication token or user ID found");
                navigate('/login');
                return;
            }

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            let platformAlerts = [];
            let syslogAlerts = [];

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_CYBEDEFENDER_AI_URL}/cybedefender/alerts?userId=${userId}&limit=100`,
                    { method: 'GET', headers }
                );

                if (response.ok) {
                    const data = await response.json();
                    const rawAlerts = Array.isArray(data)
                        ? data
                        : Array.isArray(data.alerts)
                        ? data.alerts
                        : [];

                    platformAlerts = rawAlerts.map((item, idx) => formatMergedAlert(item, 'platform', idx));
                }
            } catch (err) {
                console.error('Error fetching platform alerts:', err);
            }

            if (activeOrganization?.guid) {
                try {
                    const query = new URLSearchParams({
                        organization_guid: activeOrganization.guid,
                        limit: '100'
                    });
                    const response = await fetch(
                        `${process.env.REACT_APP_CYBEDEFENDER_AI_URL}/cybedefender/syslog-alerts?${query.toString()}`,
                        { method: 'GET', headers }
                    );
                    if (response.ok) {
                        const data = await response.json();
                        const rawAlerts = Array.isArray(data) ? data : [];
                        syslogAlerts = rawAlerts.map((item, idx) => formatMergedAlert(item, 'syslog', idx));
                    }
                } catch (err) {
                    console.error('Error fetching tenant syslog alerts:', err);
                }
            }

            const sortedAlerts = [...platformAlerts, ...syslogAlerts].sort(
                (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
            );

            setAlerts(sortedAlerts);
        } catch (err) {
            setAlertsError(err.message || 'Failed to load alerts');
            console.error('Error fetching alerts:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUploadHistory = async () => {
        setLoading(true);
        setUploadHistoryError(null);
        const token = Cookies.get('token');
        const userId = Cookies.get('guid');
        try {
            const response = await axios.get(`${process.env.REACT_APP_CYBEDEFENDER_AI_URL}/cybedefender/upload-history`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    userId,
                },
            });
            setUploadHistory(response.data);
        } catch (err) {
            setUploadHistoryError('Error fetching upload history');
        } finally {
            setLoading(false);
        }
    };

    const paginatedUploadHistory = uploadHistory.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'Upload Successful':
                return 'text-green-600';
            case 'Upload Failed':
                return 'text-red-600';
            default:
                return 'text-gray-600';
        }
    };

    const getSeverityClass = (severity, defaultClass = "bg-gray-100 text-gray-800") => {
        const value = String(severity || '').toLowerCase();
        const classes = {
            low: "bg-blue-100 text-blue-800",
            medium: "bg-yellow-100 text-yellow-800",
            high: "bg-red-100 text-red-800",
            critical: "bg-red-100 text-red-800",
            normal: "bg-green-100 text-green-800",
        };
        return classes[value] || defaultClass;
    };

    const getSourceClass = (source) => {
        const value = String(source || '').toLowerCase();
        if (value === 'syslog') return 'bg-blue-100 text-blue-800';
        return 'bg-purple-100 text-purple-800';
    };

    const handleFilterChange = (field, value) => {
        if (field === "dateRange") {
            const start = value?.start ? new Date(value.start) : null;
            const end = value?.end ? new Date(value.end) : null;

            setFilters((prevFilters) => ({
                ...prevFilters,
                dateRange: {
                    start: start instanceof Date && !isNaN(start) ? start : null,
                    end: end instanceof Date && !isNaN(end) ? end : null,
                },
            }));
        } else {
            setFilters((prevFilters) => ({
                ...prevFilters,
                [field]: value,
            }));
        }
        setCurrentPage(1);
    };

    const filteredAlerts = alerts.filter((alert) => {
        const matchesSeverity = filters.severity ? String(alert.severity).toLowerCase() === String(filters.severity).toLowerCase() : true;
        const matchesThreatType = filters.threatType ? alert.threatType === filters.threatType : true;
        const matchesSource = filters.source ? String(alert.source).toLowerCase() === String(filters.source).toLowerCase() : true;
        const matchesStatus = filters.status ? String(alert.status || '').toLowerCase() === String(filters.status).toLowerCase() : true;
        const matchesDevice = filters.device
            ? [alert.collectorDevice, alert.sourceDevice].some((value) => String(value || '').toLowerCase() === String(filters.device).toLowerCase())
            : true;
        const matchesSourceIp = filters.sourceIp
            ? String(alert.sourceIp || '').toLowerCase().includes(String(filters.sourceIp).trim().toLowerCase())
            : true;

        const alertDate = alert.date ? new Date(alert.date).getTime() : null;
        const startDate = filters.dateRange.start instanceof Date && !isNaN(filters.dateRange.start) ? filters.dateRange.start.getTime() : null;
        const endDate = filters.dateRange.end instanceof Date && !isNaN(filters.dateRange.end) ? filters.dateRange.end.getTime() : null;

        const matchesDateRange =
            (!startDate || (alertDate && alertDate >= startDate)) &&
            (!endDate || (alertDate && alertDate <= endDate));

        return matchesSeverity && matchesThreatType && matchesSource && matchesStatus && matchesDevice && matchesSourceIp && matchesDateRange;
    });

    const paginatedAlerts = filteredAlerts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePagination = (direction) => {
        setCurrentPage((prevPage) => prevPage + direction);
    };

    return (
        <AppLayout onLogout={onLogout}>
                        <div className="flex items-center justify-between mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center ">
                    <div className="bg-gray-100 rounded-lg p-1 flex items-center">
                        <button
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative m-0 ${
                                activeTab === 'all-threats'
                                    ? 'bg-white text-gray-900'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            onClick={() => handleTabChange('all-threats')}
                        >
                            All Threats
                        </button>

                        <button
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative m-0 ${
                                activeTab === 'upload-history'
                                    ? 'bg-white text-gray-900'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            onClick={() => handleTabChange('upload-history')}
                        >
                            Upload History
                        </button>
                    </div>
                </div>
                <>
                    <button
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg m-0 hover:bg-blue-700 transition-colors flex items-center"
                        onClick={() => setIsUploadModalOpen(true)}
                    >
                        Upload
                    </button>
                    <FileUploadModal
                        isOpen={isUploadModalOpen}
                        onClose={() => setIsUploadModalOpen(false)}
                        onUploadSuccess={fetchAlerts}
                    />
                </>
            </div>

            {activeTab === 'upload-history' && (
                <div className="bg-white rounded-lg shadow">
                    <div className="overflow-x-auto">
                        {loading ? (
                            <NoAlertsState message="Loading..." />
                        ) : uploadHistoryError ? (
                            <NoAlertsState message={uploadHistoryError} />
                        ) : uploadHistory.length > 0 ? (
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="text-left p-4 text-gray-600 font-medium">File Name</th>
                                        <th className="text-left p-4 text-gray-600 font-medium">Timestamp</th>
                                        <th className="text-left p-4 text-gray-600 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedUploadHistory.map((item, index) => (
                                        <tr key={index} className="border-b">
                                            <td className="p-4 text-gray-900">{item.fileName}</td>
                                            <td className="p-4 text-gray-900">{item.timestamp}</td>
                                            <td className={`p-4 ${getStatusColor(item.status)}`}>{item.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <NoAlertsState message="No History Found" />
                        )}
                    </div>

                    {!loading && !uploadHistoryError && uploadHistory.length > 0 && (
                        <div className="p-4 border-t flex justify-between items-center text-sm text-gray-600">
                            <button
                                onClick={() => handlePagination(-1)}
                                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300"
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span>
                                Page {currentPage} of {Math.ceil(uploadHistory.length / itemsPerPage)}
                            </span>
                            <button
                                onClick={() => handlePagination(1)}
                                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300"
                                disabled={currentPage === Math.ceil(uploadHistory.length / itemsPerPage)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'all-threats' && (
                <div className="bg-white rounded-lg shadow">
                    <div className="p-4 border-b">
                        <div className="flex justify-between items-center">
                            <div className="flex gap-4 flex-wrap">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Severity:</span>
                                    <CybeSelect
                                        className="flex items-center gap-1 px-3 py-1.5 m-0 border rounded-md hover:bg-gray-50"
                                        onChange={(e) => handleFilterChange("severity", e.target.value)}
                                        value={filters.severity}
                                    >
                                        {severityOptions.map((option, index) => (
                                            <option key={index} value={option === "All" ? "" : option.toLowerCase()}>
                                                {option}
                                            </option>
                                        ))}
                                    </CybeSelect>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Threat Type:</span>
                                    <CybeSelect
                                        className="flex items-center gap-1 px-3 py-1.5 m-0 border rounded-md hover:bg-gray-50"
                                        onChange={(e) => handleFilterChange("threatType", e.target.value)}
                                        value={filters.threatType}
                                    >
                                        {threatTypeOptions.map((option, index) => (
                                            <option key={index} value={option === "All" ? "" : option}>
                                                {option}
                                            </option>
                                        ))}
                                    </CybeSelect>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Source:</span>
                                    <CybeSelect
                                        className="flex items-center gap-1 px-3 py-1.5 m-0 border rounded-md hover:bg-gray-50"
                                        onChange={(e) => handleFilterChange("source", e.target.value)}
                                        value={filters.source}
                                    >
                                        {sourceOptions.map((option, index) => (
                                            <option key={index} value={option === "All" ? "" : option}>
                                                {option}
                                            </option>
                                        ))}
                                    </CybeSelect>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Status:</span>
                                    <CybeSelect className="px-3 py-1.5 m-0 border rounded-md hover:bg-gray-50" onChange={(e) => handleFilterChange("status", e.target.value)} value={filters.status}>
                                        {statusOptions.map((option) => <option key={option} value={option === "All" ? "" : option.toLowerCase()}>{option}</option>)}
                                    </CybeSelect>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Device:</span>
                                    <CybeSelect className="px-3 py-1.5 m-0 border rounded-md hover:bg-gray-50" onChange={(e) => handleFilterChange("device", e.target.value)} value={filters.device}>
                                        <option value="">All</option>
                                        {deviceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                                    </CybeSelect>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Source IP:</span>
                                    <input className="w-40 px-3 py-1.5 m-0 border rounded-md" value={filters.sourceIp} onChange={(e) => handleFilterChange("sourceIp", e.target.value)} placeholder="Search IP" />
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">Date Range:</span>
                                    <DateRangePicker
                                        onApply={(range) => handleFilterChange("dateRange", range)}
                                        value={filters.dateRange}
                                    />
                                </div>

                                <button
                                    className="text-gray-600 m-0 hover:text-gray-800"
                                    onClick={() => {
                                        setFilters({
                                            severity: "",
                                            threatType: "",
                                            source: "",
                                            status: "",
                                            device: "",
                                            sourceIp: "",
                                            dateRange: { start: null, end: null },
                                        });
                                        setCurrentPage(1);
                                    }}
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        {isLoading ? (
                            <div className="p-8 text-center text-gray-600">
                                Loading alerts...
                            </div>
                        ) : alertsError ? (
                            <NoAlertsState message={alertsError} />
                        ) : paginatedAlerts.length ? (
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left p-4 text-gray-600">Date</th>
                                        <th className="text-left p-4 text-gray-600">Source</th>
                                        <th className="text-left p-4 text-gray-600">Threat Type</th>
                                        <th className="text-left p-4 text-gray-600">Severity</th>
                                        <th className="text-left p-4 text-gray-600">Source IP</th>
                                        <th className="text-left p-4 text-gray-600">Device</th>
                                        <th className="text-left p-4 text-gray-600">Country</th>
                                        <th className="text-left p-4 text-gray-600">ASN</th>
                                        <th className="text-left p-4 text-gray-600">Threat Score</th>
                                        <th className="text-left p-4 text-gray-600">Flags</th>
                                        <th className="text-left p-4 text-gray-600">Rule</th>
                                        <th className="text-left p-4 text-gray-600">Status</th>
                                        <th className="text-left p-4">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedAlerts
                                        .filter(alert => alert.threatType !== "Normal")
                                        .map((alert, index) => (
                                            <tr key={alert.id || index} className="border-t">
                                                <td className="p-4 text-gray-900">{alert.date ? formatDate(alert.date) : '-'}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded-full text-sm ${getSourceClass(alert.source)}`}>
                                                        {alert.source}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-gray-900">{alert.threatType}</td>
                                                <td className="p-4">
                                                    <span className={`px-2 py-1 rounded-full text-sm ${getSeverityClass(alert.severity)}`}>
                                                        {alert.severity}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-gray-900">{alert.sourceIp || '-'}</td>
                                                <td className="p-4 text-gray-900">{alert.sourceDevice !== '-' ? alert.sourceDevice : alert.collectorDevice}</td>
                                                <td className="p-4 text-gray-900">{alert.country || '-'}</td>
                                                <td className="p-4 text-gray-900">{alert.asn || '-'}</td>
                                                <td className="p-4 text-gray-900">{alert.threatScore ?? 0}</td>
                                                <td className="p-4 text-gray-900">{alert.flags || 'None'}</td>
                                                <td className="p-4 text-gray-900">{alert.matchedRule || '-'}</td>
                                                <td className="p-4 text-gray-900">{alert.status || '-'}</td>
                                                <td className="p-4">
                                                    <button
                                                        className="px-4 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                                        onClick={() => {
                                                            setSelectedThreat(alert);
                                                            setIsDetailsModalOpen(true);
                                                        }}
                                                    >
                                                        {alert.isSyslog ? 'Investigate' : 'View Details'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        ) : (
                            <NoAlertsState message="No Alerts Found" />
                        )}
                    </div>

                    {filteredAlerts.length > 0 && (
                        <div className="p-4 border-t flex justify-between items-center text-sm text-gray-600">
                            <button
                                onClick={() => handlePagination(-1)}
                                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300"
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span>
                                Page {currentPage} of {Math.ceil(filteredAlerts.length / itemsPerPage)}
                            </span>
                            <button
                                onClick={() => handlePagination(1)}
                                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:bg-gray-300"
                                disabled={currentPage === Math.ceil(filteredAlerts.length / itemsPerPage)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            <ThreatDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                threat={selectedThreat}
                onUpdated={fetchAlerts}
            />
                    </AppLayout>
    );
};

export default Alerts;
